import { HStack, Heading, TextProps } from "@chakra-ui/react"
import UserAvatarStack from "@pathwright/ux/src/components/avatar/UserAvatarStack"
import { AvatarUser } from "@pathwright/ux/src/components/avatar/types"
import IntersectionLazyLoad, {
  IntersectionLazyLoadProps
} from "@pathwright/ux/src/components/loading/IntersectionLazyLoad"
import {
  HomeCohortMembersFragment,
  useHomeCohortMembersLazyQuery
} from "../../api/generated"

export function GroupName(props: TextProps) {
  return <Heading as="h3" size="xs" fontWeight="medium" m={0} {...props} />
}

type GroupAvatarsProps = {
  group: HomeCohortMembersFragment
  showGroupName?: boolean
  fontColor?: "whiteAlpha.900" | "blackAlpha.900"
  noOfLines?: number
}

// Dumb component that accepts the group.
export const GroupAvatars = ({
  group,
  showGroupName,
  fontColor = "whiteAlpha.900",
  noOfLines
}: GroupAvatarsProps) => {
  const users: AvatarUser[] = []
  // Push the user's registration into the first position.
  if (group.user_registration) users.push(group!.user_registration!.user!)
  // Push all other users registrations after, filtering out the user's
  // registraiton if found in the list.
  users.push(
    ...(group.registrations?.edges
      ?.map((edge) => edge?.node!.user!)
      .filter((node) => node.id !== users[0]?.id) || [])
  )

  return (
    (users.length || showGroupName) && (
      <HStack pos="relative" spacing={2}>
        {users.length && (
          <UserAvatarStack users={users} borderColor={fontColor} />
        )}
        {showGroupName && (
          <GroupName color={fontColor} noOfLines={noOfLines}>
            {group.name}
          </GroupName>
        )}
      </HStack>
    )
  )
}

type GroupAvatarsContainerProps = {
  groupId: number
  fallback?: IntersectionLazyLoadProps["fallback"]
} & Omit<GroupAvatarsProps, "group">

// Container component that queries the group by id.
const GroupAvatarsContainer = ({
  groupId,
  fallback,
  ...rest
}: GroupAvatarsContainerProps) => {
  return (
    <IntersectionLazyLoad
      lazyQuery={useHomeCohortMembersLazyQuery({
        variables: {
          groupId: groupId
        }
      })}
      fallback={fallback}
    >
      {([_load, { data }]) => <GroupAvatars group={data.group} {...rest} />}
    </IntersectionLazyLoad>
  )
}

export default GroupAvatarsContainer
